import React, { useState } from 'react'
import Panel from '@components/common/Panel'
import services from '@services'
import Button from '@components/common/form/Button'
import YSpacing from '@components/common/YSpacing'
import {
  ScheduleOverride,
  OverrideType,
  AddReason,
  CancelReason,
} from '../types'
import OverridesList from './OverridesList'
import AddOverrideModal from './AddOverrideModal'

interface Props {
  overrides: ScheduleOverride[]
  onAddOverride: (data: {
    date: string
    overrideType: OverrideType
    reason: AddReason | CancelReason | null
    notes: string | null
    cutoffDaysBefore: number | null
    cutoffTime: number | null
    dropoffTime: number | null
  }) => void
  onRemoveOverride: (id: string) => void
  // Passed from calendar click
  pendingOverride?: {
    date: string
    type: OverrideType
    existing?: ScheduleOverride
  } | null
  onClearPendingOverride?: () => void
}

const OverridesPanel: React.FC<Props> = ({
  overrides,
  onAddOverride,
  onRemoveOverride,
  pendingOverride,
  onClearPendingOverride,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalInitialDate, setModalInitialDate] = useState<string | undefined>()
  const [modalInitialType, setModalInitialType] = useState<
    OverrideType | undefined
  >()
  const [modalExistingOverride, setModalExistingOverride] = useState<
    ScheduleOverride | undefined
  >()

  const openModal = (
    date?: string,
    type?: OverrideType,
    existing?: ScheduleOverride,
  ) => {
    setModalInitialDate(date)
    setModalInitialType(type)
    setModalExistingOverride(existing)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalInitialDate(undefined)
    setModalInitialType(undefined)
    setModalExistingOverride(undefined)
    onClearPendingOverride?.()
  }

  // Open modal when a pending override comes from the calendar
  React.useEffect(() => {
    if (pendingOverride) {
      openModal(
        pendingOverride.date,
        pendingOverride.type,
        pendingOverride.existing,
      )
    }
  }, [pendingOverride])

  const handleSubmit = (data: {
    date: string
    overrideType: OverrideType
    reason: AddReason | CancelReason | null
    notes: string | null
    cutoffDaysBefore: number | null
    cutoffTime: number | null
    dropoffTime: number | null
  }) => {
    // Edit mode: remove the old override before adding the updated one
    if (modalExistingOverride) {
      onRemoveOverride(modalExistingOverride.id)
    }
    onAddOverride(data)
    closeModal()
  }

  return (
    <div>
      <Panel width="100%" heading="Schedule Overrides (Ad Hoc)">
        <Button size="sm" label="+ Add Override" onClick={() => openModal()} />
        <YSpacing height="12px" />
        <OverridesList overrides={overrides} onRemove={onRemoveOverride} />
      </Panel>

      {modalOpen && (
        <AddOverrideModal
          initialDate={modalInitialDate}
          initialType={modalInitialType}
          initialOverride={modalExistingOverride}
          onSubmit={handleSubmit}
          onDelete={
            modalExistingOverride
              ? async () => {
                  const confirmed =
                    await services.UIService.ConfirmationModal.show({
                      title: 'Delete Override',
                      text: 'Are you sure you want to remove this ad hoc override?',
                    })
                  if (confirmed) {
                    onRemoveOverride(modalExistingOverride.id)
                    closeModal()
                  }
                }
              : undefined
          }
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default OverridesPanel
